import React, { useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { ArrowLeft, GitBranch, Pencil, Plus, Trash2, X } from 'lucide-react'
import { getQuestionAnswerPathOptions, getQuestionAnswerValueOptions, type Question } from './QuestionBuilder'
import { stripHtml } from '../lib/surveyPresentation'

type BranchComparisonOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'not_contains'
  | 'greater_than'
  | 'less_than'
  | 'greater_or_equal'
  | 'less_or_equal'
  | 'is_answered'
  | 'not_answered'

const BRANCH_COMPARISON_OPTIONS: Array<{ value: BranchComparisonOperator; label: string }> = [
  { value: 'equals', label: 'Resposta for igual a' },
  { value: 'not_equals', label: 'Resposta for diferente de' },
  { value: 'contains', label: 'Resposta contiver o valor' },
  { value: 'not_contains', label: 'Resposta nao contiver o valor' },
  { value: 'greater_than', label: 'Resposta for maior que' },
  { value: 'less_than', label: 'Resposta for menor que' },
  { value: 'greater_or_equal', label: 'Resposta for maior ou igual a' },
  { value: 'less_or_equal', label: 'Resposta for menor ou igual a' },
  { value: 'is_answered', label: 'Pergunta estiver respondida' },
  { value: 'not_answered', label: 'Pergunta estiver vazia' },
]

const isPresenceComparison = (comparison?: string) => comparison === 'is_answered' || comparison === 'not_answered'

interface BranchConditionDraft {
  questionId: string
  answerPath?: string
  answerValue: string
  comparison?: BranchComparisonOperator
  operator?: 'AND' | 'OR'
}

interface QuestionBranchDraft {
  id: string
  sourceQuestionId: string
  targetType: 'question' | 'complete' | 'cancel_filter'
  targetQuestionId: string
  conditions: BranchConditionDraft[]
  isMultiple: boolean
  unconditional: boolean
}

interface QuestionBranchManagerProps {
  questions: Question[]
  onQuestionsChange: (questions: Question[]) => void
  onClose: () => void
}

const QUESTION_TYPES_WITH_OPTIONS = new Set([
  'single',
  'multiple',
  'checkbox',
  'dropdown',
  'ranking',
  'classification',
  'bestworst',
])

const generateId = () => `branch_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`

const hasSelectableOptions = (question: Question) =>
  (question.options?.length || 0) > 0 ||
  QUESTION_TYPES_WITH_OPTIONS.has(question.type) ||
  getQuestionAnswerPathOptions(question).length > 0

const toDraftBranches = (questions: Question[]): QuestionBranchDraft[] => {
  const drafts: QuestionBranchDraft[] = []

  questions.forEach((question) => {
    ;((question as any).conditionalBranches || []).forEach((branch: any) => {
      if (!branch?.targetQuestionId && !branch?.targetType) return

      const rawConditions = Array.isArray(branch.conditions) && branch.conditions.length > 0
        ? branch.conditions
        : [{
          questionId: question.id,
          value: branch.optionId ?? '',
          operator: 'equals'
        }]

      const normalizedConditions = rawConditions.map((condition: any, index: number) => ({
        questionId: String(condition.questionId || question.id),
        answerPath: String(condition.answerPath || condition.itemId || condition.rowId || condition.fieldId || '') || undefined,
        answerValue: String(condition.value ?? condition.optionId ?? ''),
        comparison: (String(condition.operator || 'equals') as BranchComparisonOperator),
        operator: index === 0 ? undefined : (String(condition.logicalOperator || branch.logic || 'AND') as 'AND' | 'OR')
      }))

      drafts.push({
        id: String(branch.id || generateId()),
        sourceQuestionId: question.id,
        targetType: branch.targetType || (branch.targetQuestionId ? 'question' : 'complete'),
        targetQuestionId: String(branch.targetQuestionId),
        conditions: normalizedConditions,
        isMultiple: normalizedConditions.length > 1,
        unconditional: !Array.isArray(branch.conditions) || branch.conditions.length === 0
      })
    })
  })

  return drafts
}

const QuestionBranchManager: React.FC<QuestionBranchManagerProps> = ({
  questions,
  onQuestionsChange,
  onClose
}) => {
  const selectableQuestions = useMemo(
    () => questions.filter((question) => stripHtml(question.title || '').trim().length > 0),
    [questions]
  )
  const [branches, setBranches] = useState<QuestionBranchDraft[]>(() => toDraftBranches(questions))
  const [editingBranch, setEditingBranch] = useState<QuestionBranchDraft | null>(null)
  const [showEditor, setShowEditor] = useState(false)

  const getBranchQuestionLabel = (question?: Question) => (
    question?.variable?.trim() || (question ? stripHtml(question.title || 'Pergunta sem título') : 'Pergunta não encontrada')
  )

  const getQuestionLabel = (questionId: string) => {
    const question = questions.find((item) => item.id === questionId)
    return getBranchQuestionLabel(question)
  }

  const beginNewBranch = () => {
    const firstQuestionId = selectableQuestions[0]?.id || ''
    setEditingBranch({
      id: generateId(),
      sourceQuestionId: firstQuestionId,
      targetType: 'question',
      targetQuestionId: '',
      conditions: [{
        questionId: firstQuestionId,
        answerValue: '',
        comparison: 'equals'
      }],
      isMultiple: false,
      unconditional: false
    })
    setShowEditor(true)
  }

  const beginEditBranch = (branch: QuestionBranchDraft) => {
    setEditingBranch({
      ...branch,
      conditions: branch.conditions.map((condition) => ({ ...condition }))
    })
    setShowEditor(true)
  }

  const handleDeleteBranch = (branchId: string) => {
    if (!window.confirm('Deseja excluir este desvio?')) return
    setBranches((previous) => previous.filter((branch) => branch.id !== branchId))
  }

  const updateEditingBranch = (updates: Partial<QuestionBranchDraft>) => {
    setEditingBranch((previous) => previous ? { ...previous, ...updates } : previous)
  }

  const updateCondition = (index: number, updates: Partial<BranchConditionDraft>) => {
    setEditingBranch((previous) => {
      if (!previous) return previous
      const nextConditions = [...previous.conditions]
      nextConditions[index] = { ...nextConditions[index], ...updates }
      return { ...previous, conditions: nextConditions }
    })
  }

  const addCondition = () => {
    setEditingBranch((previous) => {
      if (!previous) return previous
      return {
        ...previous,
        conditions: [
          ...previous.conditions,
          {
            questionId: previous.sourceQuestionId,
            answerValue: '',
            comparison: 'equals',
            operator: 'AND'
          }
        ]
      }
    })
  }

  const removeCondition = (index: number) => {
    setEditingBranch((previous) => {
      if (!previous || previous.conditions.length <= 1) return previous
      return {
        ...previous,
        conditions: previous.conditions.filter((_, conditionIndex) => conditionIndex !== index)
      }
    })
  }

  const handleSaveBranch = () => {
    if (!editingBranch) return

    if (!editingBranch.sourceQuestionId || (editingBranch.targetType === 'question' && !editingBranch.targetQuestionId)) {
      toast.error('Selecione a pergunta de origem e o destino do desvio.')
      return
    }

    if (editingBranch.targetType === 'question' && editingBranch.sourceQuestionId === editingBranch.targetQuestionId) {
      toast.error('A pergunta de destino precisa ser diferente da origem.')
      return
    }

    if (!editingBranch.unconditional && editingBranch.conditions.some((condition) => (
      !condition.answerValue && !isPresenceComparison(condition.comparison)
    ))) {
      toast.error('Selecione a resposta que deve disparar o desvio.')
      return
    }

    const missingAnswerPath = !editingBranch.unconditional && editingBranch.conditions.some((condition) => {
      if (isPresenceComparison(condition.comparison)) return false
      const sourceQuestion = questions.find((question) => question.id === (condition.questionId || editingBranch.sourceQuestionId))
      return getQuestionAnswerPathOptions(sourceQuestion).length > 0 && !condition.answerPath
    })
    if (missingAnswerPath) {
      toast.error('Selecione o campo/item da pergunta composta antes de salvar o desvio.')
      return
    }

    setBranches((previous) => {
      const existingIndex = previous.findIndex((branch) => branch.id === editingBranch.id)
      if (existingIndex >= 0) {
        const nextBranches = [...previous]
        nextBranches[existingIndex] = editingBranch
        return nextBranches
      }

      return [...previous, editingBranch]
    })

    setShowEditor(false)
    setEditingBranch(null)
  }

  const handleApplyBranches = () => {
    const updatedQuestions = questions.map((question) => {
      const preservedBranches = ((question as any).conditionalBranches || []).filter((branch: any) => !branch?.targetQuestionId && !branch?.targetType)

      const mappedQuestionBranches = branches
        .filter((branch) => branch.sourceQuestionId === question.id)
        .map((branch) => ({
          id: branch.id,
          targetType: branch.targetType,
          targetQuestionId: branch.targetType === 'question' ? branch.targetQuestionId : undefined,
          logic: branch.isMultiple
            ? (branch.conditions.find((condition, index) => index > 0 && condition.operator)?.operator || 'AND')
            : 'AND',
          conditions: branch.unconditional ? [] : branch.conditions.map((condition, conditionIndex) => ({
            questionId: condition.questionId || branch.sourceQuestionId,
            answerPath: condition.answerPath || undefined,
            operator: condition.comparison || 'equals',
            logicalOperator: conditionIndex === 0 ? undefined : (condition.operator || 'AND'),
            value: condition.answerValue
          }))
        }))

      const nextConditionalBranches = [...preservedBranches, ...mappedQuestionBranches]

      return {
        ...question,
        conditionalBranches: nextConditionalBranches.length > 0 ? nextConditionalBranches : undefined
      }
    })

    onQuestionsChange(updatedQuestions)
    onClose()
  }

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4"
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-blue-50 px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Desvios de Perguntas</h2>
            <p className="mt-1 text-sm text-gray-600">
              Configure pulos entre perguntas com base em respostas especificas.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-white hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {!showEditor ? (
            <div className="space-y-4">
              <button
                type="button"
                onClick={beginNewBranch}
                disabled={selectableQuestions.length === 0}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-3 text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Plus size={18} />
                <span>Novo Desvio</span>
              </button>

              {selectableQuestions.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-12 text-center text-gray-500">
                  Crie antes uma pergunta para configurar desvios.
                </div>
              ) : branches.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-12 text-center text-gray-500">
                  Nenhum desvio configurado.
                </div>
              ) : (
                <div className="space-y-3">
                  {branches.map((branch) => (
                    <div
                      key={branch.id}
                      className="rounded-xl border border-gray-200 bg-white p-4"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="mb-2 flex items-center gap-2">
                            <span className="rounded-full bg-indigo-100 px-2.5 py-1 text-xs font-medium text-indigo-700">
                              {branch.unconditional ? 'Sempre' : branch.isMultiple ? 'Multiplo' : 'Simples'}
                            </span>
                            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                              {branch.conditions.length} condicao(oes)
                            </span>
                          </div>
                          <p className="text-sm text-gray-700">
                            <strong>De:</strong> {getQuestionLabel(branch.sourceQuestionId)}
                          </p>
                          <p className="mt-1 text-sm text-gray-700">
                              <strong>Para:</strong> {branch.targetType === 'question' ? getQuestionLabel(branch.targetQuestionId) : branch.targetType === 'complete' ? 'Finalizar questionário' : 'Cancelar por filtro'}
                          </p>
                        </div>

                        <div className="flex shrink-0 items-center gap-2">
                          <button
                            type="button"
                            onClick={() => beginEditBranch(branch)}
                            className="rounded-lg p-2 text-blue-600 transition-colors hover:bg-blue-50"
                            title="Editar desvio"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteBranch(branch.id)}
                            className="rounded-lg p-2 text-red-600 transition-colors hover:bg-red-50"
                            title="Excluir desvio"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : editingBranch ? (
            <div className="space-y-6">
              <button
                type="button"
                onClick={() => {
                  setShowEditor(false)
                  setEditingBranch(null)
                }}
                className="flex items-center gap-2 text-sm font-medium text-gray-600 transition-colors hover:text-gray-800"
              >
                <ArrowLeft size={16} />
                Voltar
              </button>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Pergunta de origem
                </label>
                <select
                  value={editingBranch.sourceQuestionId}
                  onChange={(event) => updateEditingBranch({
                    sourceQuestionId: event.target.value,
                    conditions: [{
                      questionId: event.target.value,
                      answerPath: undefined,
                      answerValue: ''
                    }]
                  })}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selecione...</option>
                  {selectableQuestions.map((question) => (
                    <option key={question.id} value={question.id}>
                      {getBranchQuestionLabel(question)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between gap-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Condicoes
                  </label>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={editingBranch.unconditional}
                        onChange={(event) => updateEditingBranch({ unconditional: event.target.checked })}
                      />
                      Sempre após responder
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="radio"
                        checked={!editingBranch.isMultiple}
                        onChange={() => updateEditingBranch({
                          isMultiple: false,
                          conditions: [editingBranch.conditions[0]]
                        })}
                      />
                      Simples
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="radio"
                        checked={editingBranch.isMultiple}
                        onChange={() => updateEditingBranch({ isMultiple: true })}
                      />
                      Multiplo
                    </label>
                  </div>
                </div>

                {!editingBranch.unconditional && <div className="space-y-3">
                  {editingBranch.conditions.map((condition, index) => {
                    const selectedQuestion = questions.find((question) => question.id === (condition.questionId || editingBranch.sourceQuestionId))
                    const answerPathOptions = getQuestionAnswerPathOptions(selectedQuestion)
                    const valueOptions = getQuestionAnswerValueOptions(selectedQuestion, condition.answerPath)
                    return (
                    <div key={`${editingBranch.id}_${index}`} className="rounded-xl border border-gray-200 p-4">
                      {index > 0 && editingBranch.isMultiple && (
                        <div className="mb-3">
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                            Operador (combinar com a condicao anterior)
                          </label>
                          <select
                            value={condition.operator || 'AND'}
                            onChange={(event) => updateCondition(index, {
                              operator: event.target.value as 'AND' | 'OR'
                            })}
                            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="AND">E (AND)</option>
                            <option value="OR">Ou (OR)</option>
                          </select>
                        </div>
                      )}

                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        {answerPathOptions.length > 0 && (
                          <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700">
                              Campo/item
                            </label>
                            <select
                              value={condition.answerPath || ''}
                              onChange={(event) => updateCondition(index, {
                                answerPath: event.target.value || undefined,
                                answerValue: ''
                              })}
                              className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Resposta completa</option>
                              {answerPathOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">
                            Condicao da resposta
                          </label>
                          <select
                            value={condition.comparison || 'equals'}
                            onChange={(event) => updateCondition(index, {
                              comparison: event.target.value as BranchComparisonOperator,
                              answerValue: isPresenceComparison(event.target.value) ? '' : condition.answerValue
                            })}
                            className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            {BRANCH_COMPARISON_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        {!isPresenceComparison(condition.comparison) && (
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">
                            Se a resposta for
                          </label>
                          {valueOptions.length > 0 ? (
                            <select
                              value={condition.answerValue}
                              onChange={(event) => updateCondition(index, { answerValue: event.target.value })}
                              className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Selecione...</option>
                              {valueOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={condition.answerValue}
                              onChange={(event) => updateCondition(index, { answerValue: event.target.value })}
                              placeholder="Digite o valor"
                              className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          )}
                        </div>
                        )}
                      </div>

                      {editingBranch.isMultiple && editingBranch.conditions.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeCondition(index)}
                          className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-red-600 transition-colors hover:text-red-700"
                        >
                          <Trash2 size={14} />
                          Remover condicao
                        </button>
                      )}
                    </div>
                    )
                  })}
                </div>}

                {!editingBranch.unconditional && editingBranch.isMultiple && (
                  <button
                    type="button"
                    onClick={addCondition}
                    className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-blue-600 transition-colors hover:text-blue-700"
                  >
                    <Plus size={14} />
                    Adicionar condicao
                  </button>
                )}
              </div>

              <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Destino do desvio
                  </label>
                  <select
                  value={editingBranch.targetType === 'question' ? `question:${editingBranch.targetQuestionId}` : editingBranch.targetType}
                  onChange={(event) => {
                    const value = event.target.value
                    if (value.startsWith('question:')) {
                      updateEditingBranch({ targetType: 'question', targetQuestionId: value.slice('question:'.length) })
                    } else {
                      updateEditingBranch({ targetType: value as 'complete' | 'cancel_filter', targetQuestionId: '' })
                    }
                  }}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selecione...</option>
                  <option value="complete">Finalizar questionário</option>
                  <option value="cancel_filter">Cancelar por filtro</option>
                  {questions
                    .filter((question) => question.id !== editingBranch.sourceQuestionId)
                    .map((question) => (
                        <option key={question.id} value={`question:${question.id}`}>
                        {getBranchQuestionLabel(question)}
                      </option>
                    ))}
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditor(false)
                    setEditingBranch(null)
                  }}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSaveBranch}
                  className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-white transition-colors hover:bg-indigo-700"
                >
                  <GitBranch size={16} />
                  Salvar Desvio
                </button>
              </div>
            </div>
          ) : null}
        </div>

        {!showEditor && (
          <div className="flex justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-100"
            >
              Fechar
            </button>
            <button
              type="button"
              onClick={handleApplyBranches}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-white transition-colors hover:bg-indigo-700"
            >
              Aplicar Desvios
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default QuestionBranchManager